/**
 * Clearing-depth info / editor. A "do not cross" depth contour, pinned to the
 * isobath's charted value. Name it, turn its guard on or off, file it under a
 * pilotage plan, or delete it. The do-not-cross line is reddened on the chart;
 * the alarm fires when the charted depth under the boat falls below this value.
 * Read-only in Live, editable in Plotting, like the other pilotage tools.
 */
import { useEffect, useState } from 'react';
import { useShellStore } from '@/store/shell';
import { usePilotageStore } from '@/store/pilotage';
import { clearingDepthDisplayName } from '@/pilotage';
import { formatPosition } from '@/nav';
import { useUnitsStore, formatDepth } from '@/store/units';
import { Sheet } from './Sheet';

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="acp-kv">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

export function ClearingDepthSheet() {
  const activeId = usePilotageStore((s) => s.activeClearingDepthId);
  const clearingDepths = usePilotageStore((s) => s.clearingDepths);
  const plans = usePilotageStore((s) => s.plans);
  const updateClearingDepth = usePilotageStore((s) => s.updateClearingDepth);
  const removeClearingDepth = usePilotageStore((s) => s.removeClearingDepth);
  const setActiveClearingDepth = usePilotageStore((s) => s.setActiveClearingDepth);
  const assignClearingDepthToPlan = usePilotageStore((s) => s.assignClearingDepthToPlan);
  const addPlan = usePilotageStore((s) => s.addPlan);

  const mode = useShellStore((s) => s.mode);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);
  const depthUnit = useUnitsStore((s) => s.depth);

  const cd = clearingDepths.find((x) => x.id === activeId);

  const [name, setName] = useState('');
  useEffect(() => {
    if (cd) setName(cd.name ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  if (!cd) return <Sheet name="clearingdepth" title="Clearing depth" />;

  const readOnly = mode === 'Live';
  const deepSide = cd.side === 'deep';
  const guide = deepSide
    ? `Stay shallower than ${formatDepth(cd.valueM, depthUnit)}. The alarm sounds if the charted depth under the boat rises above it, i.e. we've crossed onto the deep side of this contour.`
    : `Stay deeper than ${formatDepth(cd.valueM, depthUnit)}. The alarm sounds if the charted depth under the boat falls below it, i.e. we've crossed onto the shallow side of this contour.`;

  if (readOnly) {
    return (
      <Sheet
        name="clearingdepth"
        title="Clearing depth"
        sub="Read-only while we're in Live. Switch to Plotting to change it."
      >
        <Row k="Name" v={clearingDepthDisplayName(cd)} />
        <Row k="Do not cross" v={formatDepth(cd.valueM, depthUnit)} />
        <Row k="Alarm side" v={deepSide ? 'deep side' : 'shallow side'} />
        <Row k="Guard" v={cd.alarm ? 'on' : 'off'} />
        <Row k="Contour position" v={formatPosition(cd.anchor)} />
        <p className="sub" style={{ marginTop: 10 }}>
          {guide}
        </p>
      </Sheet>
    );
  }

  function del() {
    if (!cd) return;
    removeClearingDepth(cd.id);
    setActiveClearingDepth(null);
    closeSheet();
    showToast('Clearing depth deleted');
  }

  return (
    <Sheet
      name="clearingdepth"
      title="Clearing depth"
      sub="A do-not-cross depth contour, pinned to its charted value. The isobath is reddened on the chart and guarded by the alarm."
    >
      <Row k="Do not cross" v={formatDepth(cd.valueM, depthUnit)} />
      <p className="sub" style={{ marginTop: 6 }}>
        {guide}
      </p>

      <label className="acp-fieldlabel">Guard</label>
      <div className="acp-seg">
        <button
          type="button"
          className={cd.alarm ? 'on' : ''}
          onClick={() => updateClearingDepth(cd.id, { alarm: true })}
        >
          Alarm on
        </button>
        <button
          type="button"
          className={!cd.alarm ? 'on' : ''}
          onClick={() => updateClearingDepth(cd.id, { alarm: false })}
        >
          Alarm off
        </button>
      </div>

      <label className="acp-fieldlabel">Alarm side</label>
      <div className="acp-seg">
        <button
          type="button"
          className={!deepSide ? 'on' : ''}
          onClick={() => updateClearingDepth(cd.id, { side: 'shallow' })}
        >
          Shallow side
        </button>
        <button
          type="button"
          className={deepSide ? 'on' : ''}
          onClick={() => updateClearingDepth(cd.id, { side: 'deep' })}
        >
          Deep side
        </button>
      </div>
      <p className="sub" style={{ marginTop: 6 }}>
        Which side of the contour is the danger. Shallow side warns as we cross into shallower water (the
        usual do-not-cross); deep side warns the other way, for a contour we want to stay inside of.
      </p>

      <label className="acp-fieldlabel">Name</label>
      <input
        className="acp-text-input"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          updateClearingDepth(cd.id, { name: e.target.value });
        }}
        placeholder={clearingDepthDisplayName(cd)}
      />
      <label className="acp-fieldlabel">Pilotage plan</label>
      <div className="acp-seg" style={{ flexWrap: 'wrap' }}>
        <button
          type="button"
          className={cd.planId === null ? 'on' : ''}
          onClick={() => assignClearingDepthToPlan(cd.id, null)}
        >
          Loose
        </button>
        {plans.map((p) => (
          <button
            key={p.id}
            type="button"
            className={cd.planId === p.id ? 'on' : ''}
            onClick={() => assignClearingDepthToPlan(cd.id, p.id)}
          >
            {p.name}
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            const id = addPlan();
            assignClearingDepthToPlan(cd.id, id);
            showToast('Added to a new plan');
          }}
        >
          + New plan
        </button>
      </div>
      <div className="acp-btn-row">
        <button type="button" className="acp-btn" onClick={del}>
          Delete
        </button>
        <button
          type="button"
          className="acp-btn primary"
          onClick={() => {
            setActiveClearingDepth(null);
            closeSheet();
          }}
        >
          Done
        </button>
      </div>
    </Sheet>
  );
}
